import { Button, Combobox } from '@ibsys/kit';
import { FC, FocusEvent, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import type { ChartRuleColumn } from '@/entities/dashboards';
import { isRichTextMarkup, plainTextToRichText } from '@/shared/lib';
import { RichTextEditor, type RichTextEditorHandle, type RichTextSelection } from '@/shared/ui';
import { chartTemplatePlaces, withPlaceValue, type ChartTemplatePlace } from './chartTemplatePlaces';
import {
  hasRowSection,
  rowSectionEdits,
  rowSectionRemovalEdits,
  type ChartTemplateEdit,
} from './chartTemplateRowSection';

type Props = {
  /** Текст результата вместе с местами подстановки значений выборки */
  value: string;
  onChange: (template: string) => void;
  /** Набранный текст встаёт на диаграмму: пользователь ушёл из поля или выбрал значение места подстановки */
  onCommit: () => void;
  /** Значения выборки, доступные тексту результата */
  columns: ChartRuleColumn[];
  /** Сводка показывает всю выборку — у неё часть текста относится к отдельной её строке */
  isRowSectionShown: boolean;
};

/**
 * Текст результата показателя и сводки: сам текст, его оформление и места подстановки значений выборки.
 *
 * Текст набирается с оформлением — тем же, что и комментарий к оценке заключения: сводка по своей
 * сути перечень величин, и строками он читается, а сплошной строкой нет. Кроме того, выделенной части
 * задаётся размер: главное в тексте результата показывают крупнее подписи к нему, а комментарию это
 * не нужно.
 *
 * Значение, подставленное в выделенное место, получает его размер: размер — разметка самого текста.
 *
 * Место подстановки задаётся выбором значения из перечня, а не набором его имени: перечень
 * приходит вместе с правилом и открыт всякому, кто правит дашборд, тогда как сам запрос закрыт
 * разрешением. По каждому месту видно подставленное в него значение, и оно меняется тут же —
 * не открывая запрос и не переписывая текст руками.
 *
 * Место, значения которого в выборке нет, из перечня не пропадает: текст мог быть набран до
 * правки запроса, и пользователю нужно видеть, что подстановка осталась ни на что не указывающей.
 *
 * Повторяемая часть сводки помечается тем же способом — выделением в тексте, а не набором пометок
 * по памяти: сами пометки остаются в тексте видимыми, поэтому что именно повторяется по строкам
 * выборки, видно там же, где текст правится.
 *
 * Диаграмма принимает набранный текст, когда пользователь уходит из поля, а выбранное для места
 * значение — сразу: пока поле в фокусе, она не перестраивается на каждый набранный знак.
 */
export const ChartTemplateField: FC<Props> = ({ value, onChange, onCommit, columns, isRowSectionShown }) => {
  const { t } = useTranslation('qualityDashboard');
  const editorRef = useRef<RichTextEditorHandle>(null);
  // Место правки: последнее выделение, известное до ухода фокуса на кнопку
  const selectionRef = useRef<RichTextSelection | null>(null);

  // Текст, составленный системой или сохранённый до появления оформления, приходит без разметки
  const editorValue = useMemo(() => (isRichTextMarkup(value) ? value : plainTextToRichText(value)), [value]);

  const places = useMemo(() => chartTemplatePlaces(value), [value]);
  const isRowSectionSet = hasRowSection(value);

  const columnOptions = useMemo(
    () => columns.map((column) => ({ value: column.name, label: column.name, description: column.type })),
    [columns],
  );

  const placeOptions = (place: ChartTemplatePlace) =>
    columns.some((column) => column.name === place.name)
      ? columnOptions
      : [
          ...columnOptions,
          { value: place.name, label: place.name, description: t('presets.editor.templateUnknownValue') },
        ];

  const handleSelectionChange = (selection: RichTextSelection | null) => {
    if (selection) selectionRef.current = selection;
  };

  const applyEdits = (edits: ChartTemplateEdit[]) => {
    const editor = editorRef.current;
    if (!editor) return;

    for (const { at, length, text } of edits) editor.replaceText(at, length, text);
    editor.focus();
  };

  const insertPlace = (name: string) => {
    const editor = editorRef.current;
    if (!editor) return;

    const place = `{${name}}`;
    // Свой завершающий перенос строки редактор считает частью текста — вставке он не место
    const textEnd = editor.getText().replace(/\n$/, '').length;
    const at = selectionRef.current?.index ?? textEnd;
    editor.replaceText(at, 0, place);
    selectionRef.current = { index: at + place.length, length: 0 };
    editor.focus();
  };

  const changeRowSection = () => {
    const editor = editorRef.current;
    if (!editor) return;

    const text = editor.getText();
    applyEdits(isRowSectionSet ? rowSectionRemovalEdits(text) : rowSectionEdits(text, selectionRef.current));
  };

  const changePlace = (place: ChartTemplatePlace, name: unknown) => {
    if (typeof name !== 'string' || name === place.name) return;
    onChange(withPlaceValue(value, place, name));
    onCommit();
  };

  // Переход между текстом и его панелью оформления уходом из поля не считается
  const handleFieldBlur = (event: FocusEvent<HTMLDivElement>) => {
    if (event.currentTarget.contains(event.relatedTarget)) return;
    onCommit();
  };

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-col gap-1.5" onBlur={handleFieldBlur}>
        <p className="text-sm font-medium">{t('presets.editor.chartTemplate')}</p>
        <RichTextEditor
          ref={editorRef}
          value={editorValue}
          onChange={onChange}
          onSelectionChange={handleSelectionChange}
          editorClassName="[&_.ql-editor]:max-h-48 [&_.ql-editor]:min-h-16"
          hasSizes
        />
        <p className="text-xs text-muted-foreground">{t('presets.editor.chartTemplateHint')}</p>
      </div>

      {columns.length > 0 && (
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs text-muted-foreground">{t('presets.editor.templateValues')}</span>
          {columns.map((column) => (
            <Button key={column.name} variant="secondary" size="sm" onClick={() => insertPlace(column.name)}>
              {column.name}
            </Button>
          ))}
        </div>
      )}

      {isRowSectionShown && (
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="secondary" size="sm" onClick={changeRowSection}>
            {t(isRowSectionSet ? 'presets.editor.templateRowSectionClear' : 'presets.editor.templateRowSectionMark')}
          </Button>
          <span className="text-xs text-muted-foreground">
            {t(isRowSectionSet ? 'presets.editor.templateRowSectionMarked' : 'presets.editor.templateRowSectionHint')}
          </span>
        </div>
      )}

      {places.length > 0 && (
        <div className="grid grid-cols-1 gap-2 @md/chart-properties:grid-cols-2">
          {places.map((place, index) => (
            <Combobox
              key={`${place.start}-${place.name}`}
              label={t('presets.editor.templatePlace', { number: index + 1 })}
              value={place.name}
              options={placeOptions(place)}
              onChange={(name) => changePlace(place, name)}
              isSearchable={false}
              className="w-full"
            />
          ))}
        </div>
      )}
    </div>
  );
};
